import React, { Fragment, useState } from 'react';
import {
	SdfBadge,
	SdfBox,
	SdfButton,
	SdfExpandableBox,
	SdfShimmer,
} from "@synerg/react-components";
import { EventCodeResponseContent, OperationTabContent } from "shared-one-ux-components";
import { get } from "lodash";
import qs from "qs";
import "../apiPicker.scss";
import getDevPortalContext from "../../../utilities/devPortalContext";

export default function Operations({
	selectedSegment, handleError, handleAddToCart, addedItems, items, showEventSubscriptions, productCode
}) {
	const [operationList, setOperationList] = useState([]);

	const handleAdd = (item) => {
		handleAddToCart(item);
	}

	const VERB_TO_STATUS = {
		"GET": "in-progress",
		"POST": "complete",
		"PUT": "changed",
		"DELETE": "failed"
	};

	const operationHeaderContent = (operation, selectedSegment) => {
		const apiType = get(operation, 'type', '');
		const typeToDisplay = (type) => {
			const value = {
				meta: {
					name: 'META',
					className: 'bg-brand-tertiary-100 text-action-destructive'
				},
				codelist: {
					name: 'CODELIST',
					className: 'bg-action-help-50 text-status-new'
				},
				table: {
					name: 'TABLE LISTS',
					className: 'bg-action-confirm-100 text-action-confirm'
				}
			}[type]
			return value;
		};

		const badge = typeToDisplay(apiType);
		const method = get(operation, 'httpMethod', '').toUpperCase();

		const isAlreadyAdded = (addedItems || []).some(({ uri, httpMethod, code, canonicalUri }) => {
			if (showEventSubscriptions) {
				return code === operation.code && canonicalUri === operation.canonicalUri;
			}
			return operation.uri === uri && operation.httpMethod === httpMethod
		});

		const addToCartContainer = () => <div className="text-alert-info flex items-baseline self-center ml-auto">
			{operation.existing ?
				<SdfButton
					disabled={true}
					icon="alert-success"
					className="pl-1 font-default text-md"
					variant='text'>
					Included
				</SdfButton> :
				(isAlreadyAdded ?
					<SdfButton
						disabled={true}
						icon="alert-success"
						className="pl-1 font-default text-md"
						variant='text'>
						Added
					</SdfButton> :
					<SdfButton
						icon="action-add"
						data-sdf-squash
						className="pl-1 font-default text-md"
						variant='text'
						onClick={(event) => {
							handleAdd(operation);
						}}>
						<div data-sdf-squash={true} className='ml-1'>Add</div>
					</SdfButton>)}
		</div>;

		return <div className='flex flex-wrap operations-overflow w-auto items-center w-full'>
			{showEventSubscriptions ? <Fragment>
				<div className='xl:max-w-8/12 lg:max-w-7/12 md:max-w-5/12 sm:max-w-1/4'>{operation.code}</div>
				{addToCartContainer()}
			</Fragment> : <Fragment>
				<SdfBadge className='w-20' status={VERB_TO_STATUS[method] || "default"}>{method}</SdfBadge>
				<div className='xl:max-w-8/12 lg:max-w-7/12 md:max-w-5/12 sm:max-w-1/4'>
					{selectedSegment === 1 ? operation.uri : operation.text}
				</div>
				<div className="font-normal ml-auto text-heading-02 flex gap-1">
					{badge &&
						<SdfBox
							variant="filled"
							fill="shaded"
							shape="rounded"
							layout="horizontal"
							className={`${badge ? badge.className : ""} items-center font-normal`}
							style={{ height: '10px' }}>
							{apiType ? badge.name : ''}
						</SdfBox>}
					{addToCartContainer()}
				</div>
			</Fragment>}
		</div>
	}

	const onApiExpandBox = async (operation, index) => {
		try {
			// check if operations content (details) is not cached
			if (!get(operationList[index], 'details', null)) {
				operationList[index] = {
					...operationList[index],
					open: true,
					loading: true
				};

				setOperationList([...operationList]);


				let params = {
					product: productCode
				};

				if (!showEventSubscriptions) {
					params.path = operation.uri;
					params.method = operation.httpMethod;
				} else {
					params.event = operation.code
				}

				const response = await getDevPortalContext().get(`${import.meta.env.VITE_API_V2_PATH}/operation-details`, {
					params,
					paramsSerializer: (params) => qs.stringify(params, { encode: true }),
				});

				const details = get(response, 'data', {});
				setOperationList(lastOperationList => {
					lastOperationList[index] = {
						...lastOperationList[index],
						details,
						loading: false
					};

					return [...lastOperationList];
				});
			}
		} catch (e) {

			operationList[index] = {
				...operationList[index],
				loading: false
			};

			setOperationList([...operationList]);

			const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching Operation details. Please try again.');
			handleError(errorMessage);
		}
	};

	const onExpandableBoxClose = (index) => {
		operationList[index] = {
			...operationList[index],
			open: false
		};
	}

	return (
		<div className='operation-details'>
			{items.map((operation, index) => {
				const loading = get(operationList[index], 'loading', false);
				const open = get(operationList[index], 'open', false);

				return <div key={`operation-details-${index}`} className='h-auto'>
					<SdfExpandableBox
						opened={open}
						className='mb-2'
						variant="shadowed"
						triggerPlacement="after"
						stroke="light"
						headingLevel="3"
						onSdfClose={(e) => {
							onExpandableBoxClose(index)
						}}
						onSdfOpen={(e) => {
							onApiExpandBox(operation, index)
						}}
					>
						{loading ?
							<SdfShimmer rows={4} /> :
							open && (showEventSubscriptions ?
								<EventCodeResponseContent
									key={`operation-details-content-event-${index}`}
									operationDetailsResponse={get(operationList[index], 'details.response', {})}
								/> :
								<OperationTabContent
									key={`operation-details-content-${index}`}
									operationDetails={get(operationList[index], 'details', {})}
									description={operation.description}
									customClassNamesRequestTab={'flex flex-col items-center'}
								/>)}
						<div slot="header" className="w-full flex flex-row justify-between items-start items-center justify-center">
							<div className="flex flex-row gap-4 items-start w-full">
								<span className="text-heading-04 font-bold w-full">{operationHeaderContent(operation, selectedSegment)}</span>
							</div>
						</div>
					</SdfExpandableBox>
				</div>
			})}
		</div>
	);
}
